import { useMemo, useState } from "react";
import { useCalls, useDevices, useMCPTT, useSubscribers } from "../mcptt/hooks";
import { PttRequestType, type Device } from "@mcptt/client";

/** Left rail — subscribers (users) joined with their devices.
 *  Filter chips: ALL · ONLINE · TX · OFFLINE (per design guide §7.4).
 *
 *  State derivation:
 *   - OFFLINE  : no live device record at all
 *   - TX       : there's a live call where SourceUserID === user.ID and
 *                state === "speaking"
 *   - ONLINE   : has a device but isn't transmitting right now */
interface Props {
  networkId: string | null;
  selectedId: string | null;
  onSelect:   (id: string) => void;
}

export function SubscribersPanel({ networkId, selectedId, onSelect }: Props) {
  const { client, snapshot } = useMCPTT();
  const users   = useSubscribers(networkId);
  const devices = useDevices(networkId);
  const calls   = useCalls();
  const [filter, setFilter] = useState<"ALL" | "ONLINE" | "OFFLINE">("ONLINE");
  const [query, setQuery]   = useState("");
  const [privatePttUserId, setPrivatePttUserId] = useState<string | null>(null);
  const [monitorIds, setMonitorIds] = useState<Set<string>>(() => new Set());

  // userId -> latest device. Prefer one with live Parameters
  // (recently-active radios usually report battery/selectedGroup, idle
  // shells often don't).
  const deviceByUser = useMemo(() => {
    const m = new Map<string, Device>();
    for (const d of devices) {
      if (!d.UserID) continue;
      const cur = m.get(d.UserID);
      if (!cur) { m.set(d.UserID, d); continue; }
      const curHasParams = !!(cur.Parameters && Object.keys(cur.Parameters).length > 0);
      const newHasParams = !!(d.Parameters && Object.keys(d.Parameters).length > 0);
      if (newHasParams && !curHasParams) m.set(d.UserID, d);
    }
    return m;
  }, [devices]);

  // Live-talker map: userId -> current call target / direction.
  const speakingByUser = useMemo(() => {
    const m = new Map<string, { targetName?: string; direction: "rx" | "tx" }>();
    for (const c of calls) {
      if (c.NetworkID !== networkId) continue;
      if (c.state !== "speaking" && c.state !== "initiated") continue;
      if (c.SourceUserID) {
        m.set(c.SourceUserID, {
          targetName: c.TargetName,
          direction: c.SourceUserID === snapshot.userId ? "tx" : "rx",
        });
      }
    }
    return m;
  }, [calls, networkId, snapshot.userId]);

  const rows = useMemo<RowData[]>(() => {
    return users
      .filter((u) => u.Type === 0)         // device users only — exclude dispatchers
      .map<RowData>((u) => {
        const dev   = deviceByUser.get(u.ID);
        const speaking = speakingByUser.get(u.ID);
        const isTx  = !!speaking;
        const state: RowData["state"] = isTx
          ? "TX"
          : dev ? "ONLINE" : "OFFLINE";
        return {
          id:            u.ID,
          login:         u.Login,
          name:          u.Name ?? u.Login,
          state,
          battery:       dev?.Parameters?.BATTERY_LEVEL,
          batteryCharging: dev?.Parameters?.BATTERY_CHARGING,
          selectedGroup: dev?.Parameters?.SelectedGroupName,
          speakingTarget: speaking?.targetName,
          deviceId:       dev?.ID,
          monitored:      monitorIds.has(u.ID),
          pttActive:      privatePttUserId === u.ID,
        };
      })
      .sort((a, b) => {
        // TX > ONLINE > OFFLINE, then alpha by name within each bucket.
        const order = { TX: 0, ONLINE: 1, OFFLINE: 2 } as const;
        if (order[a.state] !== order[b.state]) return order[a.state] - order[b.state];
        return a.name.localeCompare(b.name);
      });
  }, [users, deviceByUser, speakingByUser, monitorIds, privatePttUserId]);

  const counts = useMemo(() => {
    let online = 0, tx = 0, offline = 0;
    for (const r of rows) {
      if (r.state === "TX")          tx++;
      else if (r.state === "ONLINE") online++;
      else                           offline++;
    }
    return { all: rows.length, online, tx, offline };
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "ONLINE"  && r.state !== "ONLINE" && r.state !== "TX") return false;
      if (filter === "OFFLINE" && r.state !== "OFFLINE") return false;
      if (q && !(r.name.toLowerCase().includes(q) || r.login.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, filter, query]);

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="ph-title">Subscribers</span>
        <span className="ph-meta">{counts.online + counts.tx} / {counts.all} REGISTERED</span>
      </div>

      <div className="panel-toolbar" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
        <div className="search">
          <span style={{ color: "var(--steel-600)" }}>{"\u2315"}</span>
          <input
            placeholder="Search name or IMEI…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Chip on={filter === "ONLINE"}  onClick={() => setFilter("ONLINE")}>ONLINE · {counts.online + counts.tx}</Chip>
          <Chip on={filter === "ALL"}     onClick={() => setFilter("ALL")}>ALL · {counts.all}</Chip>
          <Chip on={filter === "OFFLINE"} onClick={() => setFilter("OFFLINE")}>OFFLINE · {counts.offline}</Chip>
        </div>
      </div>

      <div className="panel-body">
        {visible.length === 0 ? (
          <div className="empty">{rows.length === 0 ? "No subscribers in this network" : "No matches"}</div>
        ) : (
          renderGrouped(visible, filter, selectedId, onSelect, {
            onPttStart: (row) => {
              if (!networkId || !row.deviceId) return;
              setPrivatePttUserId(row.id);
              client.startPTT({
                networkId,
                destinationId: row.deviceId,
                type: PttRequestType.PrivateVoiceStart,
              });
            },
            onPttStop: (row) => {
              if (!networkId || !row.deviceId) return;
              setPrivatePttUserId(null);
              client.stopPTT({
                networkId,
                destinationId: row.deviceId,
                type: PttRequestType.PrivateVoiceStop,
              });
            },
            onMonitorToggle: (row) => {
              setMonitorIds((prev) => {
                const next = new Set(prev);
                if (next.has(row.id)) next.delete(row.id);
                else next.add(row.id);
                return next;
              });
            },
          })
        )}
      </div>
    </div>
  );
}

interface RowData {
  id: string;
  login: string;
  name: string;
  state: "OFFLINE" | "ONLINE" | "TX";
  battery: number | undefined;
  batteryCharging: boolean | undefined;
  selectedGroup: string | undefined;
  speakingTarget: string | undefined;
  deviceId: string | undefined;
  monitored: boolean;
  pttActive: boolean;
}

function Row({
  row, selected, onClick, actions,
}: {
  row: RowData;
  selected: boolean;
  onClick: () => void;
  actions: RowActions;
}) {
  const initials = (row.name || row.login)
    .split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const stateClass = row.state === "TX" ? "tx" : row.state === "ONLINE" ? "online" : "";
  // Sub-line: prefer the radio's currently-selected group; else login on
  // ONLINE (so the dispatcher can correlate with the manager's tables);
  // else "Offline" on OFFLINE.
  // Sub-line preference: live-selected group > IMEI fallback > "Offline".
  // We prefix the IMEI with the literal label so dispatchers can tell
  // it apart from a free-text callsign at a glance.
  const sub = row.speakingTarget
    ? `Talking to ${row.speakingTarget}`
    : row.selectedGroup
    ? row.selectedGroup
    : row.state === "OFFLINE"
      ? "Offline"
      : `IMEI ${row.login}`;

  return (
    <div
      className={`row dense${row.state === "TX" || selected ? " selected" : ""}${row.monitored ? " monitored" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    >
      <div className={`avatar sm ${stateClass}`}>
        {initials}
        <span className="status-dot" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline", minWidth: 0 }}>
          <span className="row-title" style={{
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            color: row.state === "TX" ? "var(--red-400)" : undefined,
          }}>
            {row.name}
          </span>
        </div>
        <div className="row-sub" style={{
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {sub}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
        {row.state !== "OFFLINE" ? (
          <div className="sub-actions">
            <button
              type="button"
              className={`sub-action mon ${row.monitored ? "on" : ""}`}
              onClick={(e) => { e.stopPropagation(); actions.onMonitorToggle(row); }}
              title="Local monitor marker (protocol monitor follow-up)"
            >
              MON
            </button>
            <button
              type="button"
              className={`sub-action ptt ${row.pttActive ? "on" : ""}`}
              onPointerDown={(e) => { e.stopPropagation(); e.currentTarget.setPointerCapture?.(e.pointerId); actions.onPttStart(row); }}
              onPointerUp={(e) => { e.stopPropagation(); try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ } actions.onPttStop(row); }}
              onPointerCancel={(e) => { e.stopPropagation(); actions.onPttStop(row); }}
              onPointerLeave={(e) => { if ((e.buttons & 1) !== 0) actions.onPttStop(row); }}
              title={`Hold for private PTT to ${row.name}`}
            >
              {row.pttActive ? "TX" : "PTT"}
            </button>
          </div>
        ) : null}
        {row.battery != null ? (
          <BatteryIcon level={row.battery} charging={!!row.batteryCharging} />
        ) : null}
        <span
          className="t-mono"
          style={{
            fontSize: 11,
            color: row.state === "TX" ? "var(--red-400)"
                 : row.state === "ONLINE" ? "var(--success-text)"
                 : "var(--steel-600)",
            letterSpacing: "0.14em",
          }}
        >
          {row.state}
        </span>
      </div>
    </div>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={`chip ${on ? "selected" : ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

function BatteryIcon({ level, charging }: { level: number; charging: boolean }) {
  const pct = Math.max(0, Math.min(100, Math.round(level)));
  const tone = pct <= 15 ? "danger" : pct <= 35 ? "warn" : "ok";
  return (
    <span
      className={`battery-icon battery-icon--${tone}${charging ? " is-charging" : ""}`}
      title={`${pct}%${charging ? " charging" : ""}`}
      aria-label={`Battery ${pct}%${charging ? " charging" : ""}`}
    >
      <span className="battery-body">
        <span className="battery-fill" style={{ width: `${pct}%` }} />
        <span className="battery-text">{pct}%</span>
        {charging ? <span className="battery-bolt">{"\u26A1"}</span> : null}
      </span>
      <span className="battery-cap" />
    </span>
  );
}

/**
 * Render rows with a sticky section header between the ONLINE-ish group
 * (TX + ONLINE) and the OFFLINE group when the operator is on ALL or
 * ONLINE filters. Single-state filters (TX-only or OFFLINE-only) skip
 * the header — the chip already says what's on screen.
 *
 * Why interleave a header into a flat list rather than render two
 * separate sections? The panel-body uses native scrolling, and a
 * sticky-positioned header keeps the dispatcher anchored to "where
 * does offline begin?" as they scroll a 200-strong roster.
 */
function renderGrouped(
  rows:       RowData[],
  filter:     "ALL" | "ONLINE" | "OFFLINE",
  selectedId: string | null,
  onSelect:   (id: string) => void,
  actions:    RowActions,
) {
  const showSplit = filter === "ALL";
  const renderRow = (r: RowData) => (
    <Row
      key={r.id}
      row={r}
      selected={r.id === selectedId}
      onClick={() => onSelect(r.id)}
      actions={actions}
    />
  );

  if (!showSplit) {
    return rows.map(renderRow);
  }
  const onlineGroup  = rows.filter((r) => r.state !== "OFFLINE");
  const offlineGroup = rows.filter((r) => r.state === "OFFLINE");

  const out: React.ReactNode[] = [];
  if (onlineGroup.length > 0) {
    out.push(<SectionHeader key="h-online" tone="online">{`ONLINE \u00B7 ${onlineGroup.length}`}</SectionHeader>);
    onlineGroup.forEach((r) => out.push(renderRow(r)));
  }
  if (offlineGroup.length > 0) {
    out.push(<SectionHeader key="h-offline" tone="offline">{`OFFLINE \u00B7 ${offlineGroup.length}`}</SectionHeader>);
    offlineGroup.forEach((r) => out.push(renderRow(r)));
  }
  return out;
}

function SectionHeader({ tone, children }: { tone: "online" | "offline"; children: React.ReactNode }) {
  return (
    <div className={`section-head section-head--${tone}`}>
      <span className="section-head-dot" />
      <span>{children}</span>
    </div>
  );
}

interface RowActions {
  onPttStart: (row: RowData) => void;
  onPttStop: (row: RowData) => void;
  onMonitorToggle: (row: RowData) => void;
}
